import './payment-surfaces.css';
import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ApiRequestError } from '../../api/client.ts';
import { paymentApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, Select, TextInput } from '../ui.tsx';
import { companyHasPlanFeature } from '../plan-gate.tsx';
import { invoiceOptionsCopy } from '@shared/invoice-options-copy.ts';
import {
  configuredInvoicePaymentOptions,
  emptyInvoicePaymentOption,
  INVOICE_OPTION_PROVIDERS,
  INVOICE_OPTION_PROVIDER_LABELS,
  saveInvoiceOptionsSchema,
  validInvoicePaymentUrl,
  type InvoiceOptionsResponse,
  type InvoicePaymentOption,
} from '@shared/invoice-payment-options.ts';
import type { CompanyPaymentMethod } from '@shared/types/index.ts';

export default function InvoiceOptionsSection() {
  const { company, user, currentLocation } = useSession();
  if (!company || !user || user.role !== 'owner' || !companyHasPlanFeature(company, 'invoicing')) return null;
  return <InvoiceOptionsScope key={`${company.id}:${user.id}:${currentLocation?.id}`} />;
}

function InvoiceOptionsScope() {
  const { company, locations } = useSession();
  const { i18n } = useTranslation();
  const copy = invoiceOptionsCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const [scope, setScope] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const path = `/payments/invoice-options${scope ? `?locationId=${encodeURIComponent(scope)}` : ''}`;
  const query = useQuery({
    queryKey: ['invoice-options', company!.id, scope],
    queryFn: () => api.get<InvoiceOptionsResponse>(path),
  });
  const methods = useQuery({
    queryKey: ['payment-methods', company!.id, 'invoice-options'],
    queryFn: () => paymentApi.listMethods(true),
  });
  return (
    <section className="card work-section payment-leaf mt-4" aria-label={copy.setup}>
      <div className="work-section-heading">
        <h2 className="mb-3 text-lg font-semibold">{copy.setup}</h2>
      </div>
      <Select
        label={copy.scope}
        value={scope}
        disabled={saving}
        onChange={(event) => {
          if (event.target.value === scope) return;
          if (dirty && !window.confirm(copy.discardScope)) return;
          setDirty(false);
          setScope(event.target.value);
        }}
        options={[
          { value: '', label: copy.company },
          ...(companyHasPlanFeature(company!, 'multi_location')
            ? locations.map((location) => ({ value: location.id, label: location.name }))
            : []),
        ]}
      />
      {(query.isError || methods.isError) && (
        <Alert tone="error">
          {copy.loadFailed}
          <Button
            onClick={() => {
              void query.refetch();
              void methods.refetch();
            }}
          >
            {copy.retry}
          </Button>
        </Alert>
      )}
      {query.data &&
      methods.data &&
      query.data.companyId === company!.id &&
      query.data.locationId === (scope || null) ? (
        <InvoiceOptionsEditor
          key={`${company!.id}:${scope}`}
          initial={query.data}
          methods={methods.data.items}
          path={path}
          onSaved={() => void query.refetch()}
          onDirtyChange={setDirty}
          onSavingChange={setSaving}
        />
      ) : null}
    </section>
  );
}

function InvoiceOptionsEditor({
  initial,
  methods,
  path,
  onSaved,
  onDirtyChange,
  onSavingChange,
}: {
  initial: InvoiceOptionsResponse;
  methods: CompanyPaymentMethod[];
  path: string;
  onSaved: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onSavingChange: (saving: boolean) => void;
}) {
  const { i18n } = useTranslation();
  const copy = invoiceOptionsCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const [mode, setMode] = useState(initial.mode);
  const [version, setVersion] = useState(initial.version);
  const [options, setOptions] = useState<InvoicePaymentOption[]>(() => [
    ...initial.options,
    ...methods
      .filter((method) => !initial.options.some((option) => option.methodId === method.id))
      .map((method) => emptyInvoicePaymentOption(method.id)),
  ]);
  // Payment methods can be added in the sibling form without remounting this
  // editor. Append their off defaults; never replace an existing draft/order.
  useEffect(() => {
    setOptions((current) => {
      const added = methods.filter((method) => !current.some((option) => option.methodId === method.id));
      return added.length ? [...current, ...added.map((method) => emptyInvoicePaymentOption(method.id))] : current;
    });
  }, [methods]);
  const [dirty, setDirty] = useState(false);
  const edited = () => {
    setSaved(false);
    setDirty(true);
    onDirtyChange(true);
  };
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const adoptResponse = (response: InvoiceOptionsResponse, saved: boolean) => {
    setMode(response.mode);
    setOptions((current) => [
      ...response.options,
      ...[...new Set([...current.map((option) => option.methodId), ...methods.map((method) => method.id)])]
        .filter((id) => !response.options.some((option) => option.methodId === id))
        .map(emptyInvoicePaymentOption),
    ]);
    setVersion(response.version);
    setSaved(saved);
    setError('');
    setDirty(false);
    onDirtyChange(false);
  };
  const update = (id: string, patch: Partial<InvoicePaymentOption>) => {
    edited();
    setOptions((current) => current.map((option) => (option.methodId === id ? { ...option, ...patch } : option)));
  };
  const save = useMutation({
    mutationFn: () =>
      api.put<InvoiceOptionsResponse>(path, { mode, options: configuredInvoicePaymentOptions(options), version }),
    onSuccess: (response) => {
      adoptResponse(response, true);
      onSaved();
    },
    onError: (error) => setError(error instanceof ApiRequestError ? `${copy.failed} ${error.message}` : copy.failed),
  });
  const reload = useMutation({
    mutationFn: () => api.get<InvoiceOptionsResponse>(path),
    onSuccess: (response) => {
      adoptResponse(response, false);
      onSaved();
    },
    onError: () => setError(copy.loadFailed),
  });
  useEffect(() => {
    onSavingChange(save.isPending || reload.isPending);
  }, [save.isPending, reload.isPending, onSavingChange]);
  useEffect(() => {
    // A clean draft follows newer same-scope settings. Do not roll back a
    // successful write when an older query response arrives, or erase edits.
    if (!dirty && !save.isPending && !reload.isPending && initial.version > version) {
      adoptResponse(initial, false);
    }
  }, [initial, version, dirty, save.isPending, reload.isPending, methods]);
  const preview =
    mode === 'inherit'
      ? initial.companyOptions
      : mode === 'hide'
        ? []
        : options.filter(
            (option) =>
              option.showOnInvoices && methods.some((method) => method.id === option.methodId && method.isActive),
          );
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (save.isPending || reload.isPending) return;
        setError('');
        const parsed = saveInvoiceOptionsSchema.safeParse({
          mode,
          options: configuredInvoicePaymentOptions(options),
          version,
        });
        if (!parsed.success) {
          setError(parsed.error.issues.map((issue) => issue.message).join(' '));
          return;
        }
        save.mutate();
      }}
    >
      <p className="my-3 text-sm">{copy.legacy}</p>
      {!initial.locationId && (
        <p className="my-3 text-sm">
          {copy.impact} {initial.inheritingLocations.join(', ') || '—'}
        </p>
      )}
      {error && (
        <Alert tone="error">
          {error}
          <Button
            disabled={save.isPending || reload.isPending}
            onClick={() => {
              if (dirty && !window.confirm(copy.discardReload)) return;
              reload.mutate();
            }}
          >
            {copy.retry}
          </Button>
        </Alert>
      )}
      {saved && <Alert tone="success">{copy.saved}</Alert>}
      {dirty && <p role="status">{copy.unsaved}</p>}
      <fieldset disabled={save.isPending || reload.isPending}>
        {initial.locationId && (
          <Select
            label={copy.scope}
            value={mode}
            onChange={(event) => {
              edited();
              setMode(event.target.value as typeof mode);
            }}
            options={['inherit', 'override', 'hide'].map((value) => ({
              value,
              label: copy[value as 'inherit' | 'override' | 'hide'],
            }))}
          />
        )}
        {mode === 'override' &&
          options.map((option, index) => (
            <div key={option.methodId} className="my-3 rounded-lg border border-[color:var(--color-line)] p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <strong>{methods.find((method) => method.id === option.methodId)?.name}</strong>
                <span>
                  <Button
                    disabled={index === 0}
                    onClick={() => {
                      edited();
                      setOptions((current) => {
                        const next = [...current];
                        [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
                        return next;
                      });
                    }}
                  >
                    {copy.up}
                  </Button>
                  <Button
                    disabled={index === options.length - 1}
                    onClick={() => {
                      edited();
                      setOptions((current) => {
                        const next = [...current];
                        [next[index], next[index + 1]] = [next[index + 1]!, next[index]!];
                        return next;
                      });
                    }}
                  >
                    {copy.down}
                  </Button>
                </span>
              </div>
              <label className="block my-2">
                <input
                  type="checkbox"
                  checked={option.showOnInvoices}
                  onChange={(event) => update(option.methodId, { showOnInvoices: event.target.checked })}
                />{' '}
                {copy.show}
              </label>
              {!methods.find((method) => method.id === option.methodId)?.isActive && <p>{copy.inactive}</p>}
              {option.showOnInvoices && (
                <>
                  <Select
                    label={copy.provider}
                    value={option.provider}
                    options={INVOICE_OPTION_PROVIDERS.map((value) => ({
                      value,
                      label:
                        value === 'custom' && i18n.language.startsWith('es')
                          ? 'Personalizado'
                          : INVOICE_OPTION_PROVIDER_LABELS[value],
                    }))}
                    onChange={(event) =>
                      update(option.methodId, {
                        provider: event.target.value as InvoicePaymentOption['provider'],
                        url: '',
                        bankQrDataUrl: '',
                        recipientInstructions: '',
                        customHost: '',
                      })
                    }
                  />
                  <TextInput
                    label={copy.recipient}
                    value={option.recipientName}
                    maxLength={100}
                    onChange={(event) => update(option.methodId, { recipientName: event.target.value })}
                  />
                  {option.provider === 'zelle' ? (
                    <>
                      <TextInput
                        label={copy.zelle}
                        value={option.recipientInstructions}
                        maxLength={240}
                        onChange={(event) => update(option.methodId, { recipientInstructions: event.target.value })}
                      />
                      <label className="block my-2">
                        {copy.bankQr}
                        <input
                          type="file"
                          accept="image/png"
                          onChange={async (event) => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            if (file.size > 100000 || file.type !== 'image/png') {
                              setError(copy.bankQr);
                              return;
                            }
                            const data = await new Promise<string>((resolve, reject) => {
                              const reader = new FileReader();
                              reader.onload = () => resolve(String(reader.result));
                              reader.onerror = reject;
                              reader.readAsDataURL(file);
                            }).catch(() => '');
                            update(option.methodId, { bankQrDataUrl: data });
                          }}
                        />
                      </label>
                      {option.bankQrDataUrl && (
                        <Button onClick={() => update(option.methodId, { bankQrDataUrl: '' })}>{copy.removeQr}</Button>
                      )}
                    </>
                  ) : (
                    <>
                      <TextInput
                        label={copy.url}
                        value={option.url}
                        maxLength={1000}
                        onChange={(event) => update(option.methodId, { url: event.target.value })}
                      />
                      {option.provider === 'custom' && (
                        <TextInput
                          label={copy.host}
                          value={option.customHost}
                          maxLength={253}
                          onChange={(event) =>
                            update(option.methodId, { customHost: event.target.value.toLowerCase() })
                          }
                        />
                      )}
                      <p className="my-2 text-sm">{copy.warning}</p>
                    </>
                  )}
                  <TextInput
                    label={copy.instructions}
                    value={option.instructions}
                    maxLength={240}
                    onChange={(event) => update(option.methodId, { instructions: event.target.value })}
                  />
                </>
              )}
            </div>
          ))}
        <aside className="my-4 rounded-lg bg-[color:var(--color-surface)] p-3">
          <h3 className="font-semibold">{copy.preview}</h3>
          {!preview.length && <p>{copy.hidden}</p>}
          {preview.map((option) => (
            <div className="my-3" key={option.methodId}>
              <strong>
                {option.provider === 'custom' && i18n.language.startsWith('es')
                  ? 'Personalizado'
                  : INVOICE_OPTION_PROVIDER_LABELS[option.provider]}{' '}
                · {option.recipientName}
              </strong>
              <p>{option.recipientInstructions}</p>
              <p>{option.instructions}</p>
              {validInvoicePaymentUrl(option.url, option.provider, option.customHost) && (
                <>
                  <a
                    className="font-semibold text-[color:var(--color-brand)] underline"
                    href={option.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                  >
                    {copy.open}
                  </a>
                  <p className="break-all text-sm">{option.url}</p>
                  <p className="text-sm">{copy.warning}</p>
                </>
              )}
              {option.bankQrDataUrl && <img src={option.bankQrDataUrl} width={160} height={160} alt={copy.bankQr} />}
            </div>
          ))}
          <p className="text-sm">{copy.verify}</p>
          <p className="text-sm">{copy.pending}</p>
        </aside>
        <Button type="submit" variant="primary" busy={save.isPending}>
          {copy.save}
        </Button>
      </fieldset>
    </form>
  );
}
